import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, FileText, Truck, Wrench, AlertTriangle, AlertCircle, Package, Loader2, Camera } from 'lucide-react';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

interface Photo {
  photo_id: number;
  photo_url: string;
  thumbnail_url: string | null;
  caption: string | null;
  created_at: string;
}

interface ShiftTimes {
  day_shift: { start: string; end: string | null } | null;
  night_shift: { start: string; end: string | null } | null;
}

interface DailyReport {
  report_id: number;
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  submitted_by: string;
  crew_count: number;
  work_completed_description: string;
  issues: string | null;
  night_work_description: string | null;
  night_issues: string | null;
  weather: string;
  temperature: number;
  total_loads_in: number;
  total_loads_out: number;
  pdf_url: string | null;
  status: string;
  photos?: Photo[];
  shift_times?: ShiftTimes;
}

interface AsphaltReport {
  report_id: number;
  job_name: string;
  bradanna_job_number?: string;
  foreman_name: string;
  plant_name: string;
  work_description: string;
  truck_count: number;
  total_loads: number;
  pdf_url: string | null;
  photos?: Photo[];
  shift_times?: ShiftTimes;
}

interface TMTicket {
  ticket_id: number;
  ticket_number: string;
  job_name: string;
  employee_name: string;
  notes: string;
  equipment_count: number;
  pdf_url: string | null;
}

interface EquipmentIssue {
  issue_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  issue_description: string;
  severity: string;
  job_name: string;
  reporter_name: string;
}

interface JobNeed {
  foreman_name: string;
  job_name: string;
  job_needs_other_text: string | null;
  items: { item_name_en: string; quantity: number }[];
}

interface OvertimeByJob {
  [jobId: string]: { regular: number; overtime: number };
}

interface DailySummaryData {
  reportDate: string;
  displayDate: string;
  hasData: boolean;
  foremanReports: DailyReport[];
  adminReports: DailyReport[];
  asphaltReports: AsphaltReport[];
  tmTickets: TMTicket[];
  equipmentIssues: EquipmentIssue[];
  siltFenceIssues: any[];
  jobNeeds: JobNeed[];
  overtimeByJob?: OvertimeByJob;
}

// Combined slide for the unified slider — interleaves info cards with photos
interface InfoSlide {
  type: 'info';
  jobName: string;
  jobNumber?: string;
  reportType: string;
  submittedBy?: string;
  crewCount?: number;
  description?: string;
  weather?: string;
  temperature?: number;
  loadsIn?: number | null;
  loadsOut?: number | null;
  truckCount?: number | null;
  totalLoads?: number | null;
  dayIssues?: string | null;
  nightDescription?: string | null;
  nightIssues?: string | null;
  needs?: string[];
}
interface PhotoSlide {
  type: 'photo';
  jobName: string;
  jobNumber?: string;
  photo: Photo;
}
type Slide = InfoSlide | PhotoSlide;

// Photo Slider Modal Component
interface PhotoSliderModalProps {
  photos: Photo[];
  initialIndex: number;
  jobName: string;
  onClose: () => void;
}

const PhotoSliderModal: React.FC<PhotoSliderModalProps> = ({ photos, initialIndex, jobName, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);

  // Zoom & pan state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Touch tracking refs (avoid re-renders during gestures)
  const touchRef = useRef({
    startX: 0, startY: 0,
    lastX: 0, lastY: 0,
    startDist: 0, startScale: 1,
    isPinching: false, isSwiping: false,
    translateStart: { x: 0, y: 0 },
    lastTap: 0
  });

  const resetZoom = () => { setScale(1); setTranslate({ x: 0, y: 0 }); };

  const changePhoto = (dir: 1 | -1) => {
    const next = currentIndex + dir;
    if (next >= 0 && next < photos.length) {
      setLoading(true);
      resetZoom();
      setCurrentIndex(next);
    }
  };

  // Preload adjacent images
  useEffect(() => {
    if (currentIndex < photos.length - 1) { const img = new Image(); img.src = photos[currentIndex + 1].photo_url; }
    if (currentIndex > 0) { const img = new Image(); img.src = photos[currentIndex - 1].photo_url; }
  }, [currentIndex, photos]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') changePhoto(1);
      if (e.key === 'ArrowLeft') changePhoto(-1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, photos.length, onClose]);

  // Reset zoom on photo change
  useEffect(() => { resetZoom(); }, [currentIndex]);

  const getTouchDist = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = touchRef.current;
    if (e.touches.length === 2) {
      // Pinch start
      t.isPinching = true;
      t.isSwiping = false;
      t.startDist = getTouchDist(e.touches);
      t.startScale = scale;
      t.translateStart = { ...translate };
    } else if (e.touches.length === 1) {
      t.isPinching = false;
      t.isSwiping = false;
      t.startX = e.touches[0].clientX;
      t.startY = e.touches[0].clientY;
      t.lastX = e.touches[0].clientX;
      t.lastY = e.touches[0].clientY;
      t.translateStart = { ...translate };

      // Double-tap detection
      const now = Date.now();
      if (now - t.lastTap < 300) {
        // Double tap: toggle zoom
        if (scale > 1) {
          resetZoom();
        } else {
          setScale(2.5);
          // Zoom into tap point
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const cx = e.touches[0].clientX - rect.left - rect.width / 2;
            const cy = e.touches[0].clientY - rect.top - rect.height / 2;
            setTranslate({ x: -cx, y: -cy });
          }
        }
        t.lastTap = 0;
      } else {
        t.lastTap = now;
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const t = touchRef.current;
    if (e.touches.length === 2 && t.isPinching) {
      // Pinch zoom
      const dist = getTouchDist(e.touches);
      const newScale = Math.min(5, Math.max(1, t.startScale * (dist / t.startDist)));
      setScale(newScale);
      if (newScale <= 1) setTranslate({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && !t.isPinching) {
      const dx = e.touches[0].clientX - t.lastX;
      const dy = e.touches[0].clientY - t.lastY;
      t.lastX = e.touches[0].clientX;
      t.lastY = e.touches[0].clientY;

      if (scale > 1) {
        // Pan when zoomed
        e.preventDefault();
        setTranslate(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      } else {
        // Track for swipe
        t.isSwiping = true;
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const t = touchRef.current;
    if (t.isPinching) {
      t.isPinching = false;
      if (scale <= 1) resetZoom();
      return;
    }
    if (scale > 1) return; // Don't swipe when zoomed

    if (t.isSwiping && e.changedTouches.length === 1) {
      const diff = t.startX - e.changedTouches[0].clientX;
      if (diff > 50) changePhoto(1);
      else if (diff < -50) changePhoto(-1);
    }
    t.isSwiping = false;
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)',
      zIndex: 100000, display: 'flex', flexDirection: 'column'
    }}>
      {/* Header with job name */}
      <div style={{
        padding: '16px 20px', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <h3 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 600 }}>{jobName}</h3>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'white',
          cursor: 'pointer', padding: '8px'
        }}>
          <X size={28} />
        </button>
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', touchAction: 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {loading && (
          <Loader2 size={48} style={{ position: 'absolute', animation: 'spin 1s linear infinite' }} color="white" />
        )}
        <img
          src={photos[currentIndex].photo_url}
          onLoad={() => setLoading(false)}
          draggable={false}
          style={{
            maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
            display: loading ? 'none' : 'block',
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transition: scale === 1 ? 'transform 0.2s ease' : 'none',
            userSelect: 'none'
          }}
          alt={photos[currentIndex].caption || 'Photo'}
        />

        {/* Left arrow */}
        {currentIndex > 0 && scale <= 1 && (
          <button onClick={() => changePhoto(-1)} style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
            width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer'
          }}>
            <ChevronLeft size={32} color="white" />
          </button>
        )}

        {/* Right arrow */}
        {currentIndex < photos.length - 1 && scale <= 1 && (
          <button onClick={() => changePhoto(1)} style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
            width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer'
          }}>
            <ChevronRight size={32} color="white" />
          </button>
        )}
      </div>

      {/* Footer with counter and caption */}
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <div style={{ color: 'white', fontSize: '14px', fontWeight: 500 }}>
          {currentIndex + 1} / {photos.length}
        </div>
        {photos[currentIndex].caption && (
          <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px' }}>
            {photos[currentIndex].caption}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// Combined slider that loops through every report's photos with an info card before each job
const CombinedSliderModal: React.FC<{
  slides: Slide[];
  initialIndex: number;
  onClose: () => void;
}> = ({ slides, initialIndex, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);
  // Track which photo URLs have already finished loading so we can skip the spinner on revisit
  const loadedRef = useRef<Set<string>>(new Set());
  const next = () => setIndex(i => (i + 1) % slides.length);
  const prev = () => setIndex(i => (i - 1 + slides.length) % slides.length);

  // Preload neighbor photos so swiping/pressing arrow doesn't show a blank
  useEffect(() => {
    const preload = (offset: number) => {
      const s = slides[(index + offset + slides.length) % slides.length];
      if (s?.type === 'photo' && !loadedRef.current.has(s.photo.photo_url)) {
        const img = new Image();
        img.onload = () => loadedRef.current.add(s.photo.photo_url);
        img.src = s.photo.photo_url;
      }
    };
    // Preload up to 2 ahead and 1 behind
    preload(1); preload(2); preload(-1);
  }, [index, slides]);

  // Reset loading whenever the active slide changes; if we already loaded this one, keep it instant
  useEffect(() => {
    const cur = slides[index];
    if (cur?.type === 'photo' && loadedRef.current.has(cur.photo.photo_url)) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [index, slides]);

  // iOS hardware-back integration: push a history state when modal opens; popstate => close
  useEffect(() => {
    window.history.pushState({ photoModal: true }, '');
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // If we leave normally (X click), pop our pushed state
      if (window.history.state?.photoModal) window.history.back();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  // Pinch-to-zoom + pan + swipe (combined gesture handling)
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const touchStart = useRef({ x: 0, y: 0 });
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; pan: { x: number; y: number } } | null>(null);
  const lastTap = useRef(0);
  const MAX_ZOOM = 4;

  // Reset zoom/pan when the active slide changes
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [index]);

  const dist = (t: React.TouchList) => {
    const dx = t[0].clientX - t[1].clientX;
    const dy = t[0].clientY - t[1].clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      pinchStart.current = { dist: dist(e.touches), zoom };
      panStart.current = null;
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      touchStart.current = { x: t.clientX, y: t.clientY };
      if (zoom > 1) {
        panStart.current = { x: t.clientX, y: t.clientY, pan };
      }
      // Double-tap to toggle zoom (only on photo slides)
      const now = Date.now();
      if (slide.type === 'photo' && now - lastTap.current < 300) {
        if (zoom === 1) setZoom(2);
        else { setZoom(1); setPan({ x: 0, y: 0 }); }
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStart.current) {
      e.preventDefault();
      const ratio = dist(e.touches) / pinchStart.current.dist;
      const next = Math.max(1, Math.min(MAX_ZOOM, pinchStart.current.zoom * ratio));
      setZoom(next);
      if (next === 1) setPan({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && panStart.current && zoom > 1) {
      e.preventDefault();
      const t = e.touches[0];
      setPan({
        x: panStart.current.pan.x + (t.clientX - panStart.current.x),
        y: panStart.current.pan.y + (t.clientY - panStart.current.y),
      });
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    // If we were pinching/panning, just clean up — don't swipe to next photo
    if (pinchStart.current || panStart.current) {
      pinchStart.current = null;
      panStart.current = null;
      return;
    }
    // Otherwise treat as a swipe (only when not zoomed in)
    if (zoom === 1 && e.changedTouches.length > 0) {
      const dx = touchStart.current.x - e.changedTouches[0].clientX;
      const dy = Math.abs(touchStart.current.y - e.changedTouches[0].clientY);
      if (dy < 80 && Math.abs(dx) > 50) {
        if (dx > 0) next(); else prev();
      }
    }
  };

  if (slides.length === 0) return null;
  const slide = slides[index];
  const photoSlides = slides.filter(s => s.type === 'photo') as PhotoSlide[];
  const photoNum = slide.type === 'photo' ? photoSlides.indexOf(slide) + 1 : null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.97)',
        zIndex: 100000, display: 'flex', flexDirection: 'column',
        // Handle iPhone notch / dynamic island
        paddingTop: 'env(safe-area-inset-top, 0)',
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
      }}
    >
      {/* Header — large X always visible */}
      <div style={{
        padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {slide.jobName}
            {slide.jobNumber && <span style={{ color: '#94a3b8', marginLeft: 8, fontWeight: 500 }}>#{slide.jobNumber}</span>}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
            {slide.type === 'photo' ? `Photo ${photoNum} of ${photoSlides.length}` : 'Up next'}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            cursor: 'pointer', width: 48, height: 48, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginLeft: 8,
          }}
        >
          <X size={26} />
        </button>
      </div>

      {/* Slide body */}
      <div
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', touchAction: 'none' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {slide.type === 'photo' ? (
          <>
            {loading && (
              <Loader2
                size={48}
                color="#fff"
                style={{ position: 'absolute', animation: 'spin 1s linear infinite' }}
              />
            )}
            <img
              src={slide.photo.photo_url}
              onLoad={() => { loadedRef.current.add(slide.photo.photo_url); setLoading(false); }}
              onError={() => setLoading(false)}
              draggable={false}
              style={{
                maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', userSelect: 'none',
                opacity: loading ? 0 : 1,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: 'opacity 0.15s',
                willChange: 'transform',
              }}
              alt={slide.photo.caption || ''}
            />
            {zoom > 1 && (
              <div style={{
                position: 'absolute', top: 12, left: 12,
                background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 10px',
                borderRadius: 12, fontSize: 12, fontWeight: 600, pointerEvents: 'none',
              }}>
                {zoom.toFixed(1)}× — double-tap to reset
              </div>
            )}
          </>
        ) : (
          <div style={{ maxWidth: 520, padding: 24, color: '#fff', overflowY: 'auto' as const, maxHeight: '100%' }}>
            <div style={{ textAlign: 'center' as const, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 12, color: slide.reportType.includes('Asphalt') ? '#fb923c' : slide.reportType.includes('Admin') ? '#60a5fa' : '#4ade80' }}>
                {slide.reportType}
              </div>
              <h2 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>{slide.jobName}</h2>
              {slide.jobNumber && <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>#{slide.jobNumber}</div>}
            </div>

            {/* Top-level facts */}
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8, fontSize: 14, color: 'rgba(255,255,255,0.9)', marginBottom: 16 }}>
              {slide.submittedBy && <div>👤 <strong>{slide.submittedBy}</strong>{slide.crewCount ? ` — ${slide.crewCount} crew` : ''}</div>}
              {slide.weather && <div>🌤️ {slide.weather}{slide.temperature != null ? ` — ${slide.temperature}°F` : ''}</div>}
              {(slide.loadsIn != null || slide.loadsOut != null) && (
                <div>🚚 Loads: {slide.loadsIn ?? 0} in / {slide.loadsOut ?? 0} out</div>
              )}
              {(slide.truckCount != null || slide.totalLoads != null) && (
                <div>🚚 {slide.truckCount ?? 0} trucks · {slide.totalLoads ?? 0} loads</div>
              )}
            </div>

            {/* Day work */}
            {slide.description && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4, fontWeight: 700 }}>Day Work</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const }}>
                  {slide.description.slice(0, 360)}{slide.description.length > 360 ? '…' : ''}
                </div>
              </div>
            )}

            {/* Day issues */}
            {slide.dayIssues && (
              <div style={{ marginBottom: 14, padding: 10, background: 'rgba(239,68,68,0.12)', borderLeft: '3px solid #ef4444', borderRadius: 4 }}>
                <div style={{ fontSize: 11, color: '#fca5a5', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4, fontWeight: 700 }}>⚠️ Day Issues</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap' as const }}>{slide.dayIssues}</div>
              </div>
            )}

            {/* Night work */}
            {slide.nightDescription && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#a78bfa', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4, fontWeight: 700 }}>🌙 Night Work</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const }}>
                  {slide.nightDescription.slice(0, 360)}{slide.nightDescription.length > 360 ? '…' : ''}
                </div>
              </div>
            )}

            {/* Night issues */}
            {slide.nightIssues && (
              <div style={{ marginBottom: 14, padding: 10, background: 'rgba(239,68,68,0.12)', borderLeft: '3px solid #ef4444', borderRadius: 4 }}>
                <div style={{ fontSize: 11, color: '#fca5a5', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4, fontWeight: 700 }}>⚠️ Night Issues</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap' as const }}>{slide.nightIssues}</div>
              </div>
            )}

            {/* Job needs */}
            {slide.needs && slide.needs.length > 0 && (
              <div style={{ marginBottom: 14, padding: 10, background: 'rgba(251,191,36,0.12)', borderLeft: '3px solid #fbbf24', borderRadius: 4 }}>
                <div style={{ fontSize: 11, color: '#fcd34d', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4, fontWeight: 700 }}>📦 Job Needs</div>
                <ul style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', margin: 0, paddingLeft: 18 }}>
                  {slide.needs.slice(0, 8).map((n, i) => <li key={i} style={{ marginBottom: 2 }}>{n}</li>)}
                </ul>
              </div>
            )}

            <button
              onClick={next}
              style={{ marginTop: 8, width: '100%', padding: '12px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              View photos →
            </button>
          </div>
        )}

        {slides.length > 1 && (
          <>
            <button onClick={prev} style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }} aria-label="Previous"><ChevronLeft size={28} color="#fff" /></button>
            <button onClick={next} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }} aria-label="Next"><ChevronRight size={28} color="#fff" /></button>
          </>
        )}
      </div>

      {/* Footer caption */}
      <div style={{ padding: '12px 16px', textAlign: 'center' as const, color: '#94a3b8', fontSize: 13, flexShrink: 0 }}>
        {slide.type === 'photo' && slide.photo.caption ? slide.photo.caption : ''}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export const DailySummaryViewer: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DailySummaryData | null>(null);
  const [currentDate, setCurrentDate] = useState<string>('');

  // Combined slider state — built from all reports' photos
  const [allSlides, setAllSlides] = useState<Slide[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (!data) { setAllSlides([]); return; }
    // Build a needs lookup keyed by job_name (matches what's available in the response)
    const needsByJob = new Map<string, string[]>();
    (data.jobNeeds || []).forEach(n => {
      if (!n.job_name) return;
      const list = needsByJob.get(n.job_name) || [];
      (n.items || []).forEach(it => list.push(`${it.quantity}× ${it.item_name_en}`));
      if (n.job_needs_other_text) list.push(n.job_needs_other_text);
      needsByJob.set(n.job_name, list);
    });

    const slides: Slide[] = [];
    const addReport = (photos: Photo[] | undefined, jobName: string, jobNumber: string | undefined, info: Omit<InfoSlide, 'type' | 'jobName' | 'jobNumber'>) => {
      if (!photos || photos.length === 0) return;
      const needs = needsByJob.get(jobName);
      slides.push({ type: 'info', jobName, jobNumber, ...info, needs });
      photos.forEach(p => slides.push({ type: 'photo', jobName, jobNumber, photo: p }));
    };
    data.foremanReports.forEach(r => addReport(r.photos, r.job_name, r.bradanna_job_number, {
      reportType: 'Foreman Report',
      submittedBy: r.submitted_by, crewCount: r.crew_count,
      description: r.work_completed_description,
      weather: r.weather, temperature: r.temperature,
      loadsIn: r.total_loads_in, loadsOut: r.total_loads_out,
      dayIssues: r.issues, nightDescription: r.night_work_description, nightIssues: r.night_issues,
    }));
    data.adminReports.forEach(r => addReport(r.photos, r.job_name, r.bradanna_job_number, {
      reportType: 'Admin/PM Report',
      submittedBy: r.submitted_by, crewCount: r.crew_count,
      description: r.work_completed_description,
      weather: r.weather, temperature: r.temperature,
      loadsIn: r.total_loads_in, loadsOut: r.total_loads_out,
      dayIssues: r.issues, nightDescription: r.night_work_description, nightIssues: r.night_issues,
    }));
    data.asphaltReports.forEach(r => addReport(r.photos, r.job_name, r.bradanna_job_number, {
      reportType: 'Asphalt Report',
      submittedBy: r.foreman_name,
      description: r.work_description,
      truckCount: r.truck_count, totalLoads: r.total_loads,
    }));
    setAllSlides(slides);
  }, [data]);

  const openCombinedLightbox = (photoId: number) => {
    const idx = allSlides.findIndex(s => s.type === 'photo' && s.photo.photo_id === photoId);
    if (idx < 0) return;
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  // Get current time in EST (Eastern Standard Time)
  const getESTTime = (): Date => {
    const now = new Date();
    // Convert to EST by using toLocaleString with timeZone
    const estString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
    return new Date(estString);
  };

  // Default to today's date in EST
  const getInitialDate = (): string => {
    const estNow = getESTTime();
    const estToday = new Date(estNow);
    estToday.setHours(0, 0, 0, 0);
    return formatDateForAPI(estToday);
  };

  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const goToPreviousDay = () => {
    const current = parseDate(currentDate);
    current.setDate(current.getDate() - 1);
    setCurrentDate(formatDateForAPI(current));
  };

  const goToNextDay = () => {
    const current = parseDate(currentDate);
    const estNow = getESTTime();
    const estToday = new Date(estNow);
    estToday.setHours(0, 0, 0, 0);

    // Don't go past today (in EST)
    if (current < estToday) {
      current.setDate(current.getDate() + 1);
      setCurrentDate(formatDateForAPI(current));
    }
  };

  const canGoNext = (): boolean => {
    const current = parseDate(currentDate);
    const estNow = getESTTime();
    const estToday = new Date(estNow);
    estToday.setHours(0, 0, 0, 0);
    return current < estToday;
  };

  useEffect(() => {
    setCurrentDate(getInitialDate());
  }, []);

  useEffect(() => {
    if (!currentDate) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(API_CONFIG.ENDPOINTS.DAILY_SUMMARY_BY_DATE(currentDate));
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError(response.data.error || 'Failed to load data');
        }
      } catch (err: any) {
        console.error('Error fetching daily summary:', err);
        setError(err.response?.data?.error || 'Failed to load daily summary');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentDate]);

  const formatShiftTime = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#f59e0b';
      default: return '#64748b';
    }
  };

  const openPDF = (url: string | null) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const goToJobActivity = (jobId: number) => {
    localStorage.setItem('jobActivity_selectedJob', JSON.stringify({ jobId, timestamp: Date.now() }));
    navigate('/job-activity');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <Loader2 size={48} style={{ animation: 'spin 1s linear infinite' }} color="#64748b" />
        <p style={{ color: '#64748b' }}>Loading daily summary...</p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#000000',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        textAlign: 'center',
        borderBottom: '4px solid #dc2626'
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#ffffff', margin: 0, letterSpacing: '0.5px' }}>
          BRADANNA INC.
        </h1>
        <p style={{ fontSize: '14px', color: '#e5e7eb', margin: '8px 0 0 0', fontWeight: '500' }}>
          Daily Construction Summary
        </p>
      </div>

      {/* Date Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginBottom: '16px'
      }}>
        <button
          onClick={goToPreviousDay}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ChevronLeft size={28} color="#64748b" />
        </button>

        <label style={{ textAlign: 'center', cursor: 'pointer', position: 'relative' }} title="Tap to pick a date">
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 4 }}>
            {data?.displayDate || 'Loading...'}
          </div>
          <input
            type="date"
            value={currentDate}
            max={getInitialDate()}
            onChange={e => { if (e.target.value) setCurrentDate(e.target.value); }}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
          />
        </label>

        <button
          onClick={goToNextDay}
          disabled={!canGoNext()}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px',
            cursor: canGoNext() ? 'pointer' : 'not-allowed',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            opacity: canGoNext() ? 1 : 0.3
          }}
        >
          <ChevronRight size={28} color="#64748b" />
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#dc2626'
        }}>
          {error}
        </div>
      )}

      {data && !data.hasData && (
        <div style={{
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          padding: '32px 16px',
          textAlign: 'center',
          color: '#64748b'
        }}>
          <FileText size={48} color="#cbd5e1" style={{ marginBottom: '12px' }} />
          <p style={{ margin: 0, fontSize: '16px' }}>No reports submitted for this date</p>
        </div>
      )}

      {data && data.hasData && (
        <>
          {/* Stats Overview */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px',
            marginBottom: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '12px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              borderLeft: '4px solid #10b981'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#065f46' }}>
                {data.foremanReports.length + data.adminReports.length}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Daily Reports</div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '12px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              borderLeft: '4px solid #ea580c'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#9a3412' }}>
                {data.asphaltReports.length}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Asphalt</div>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '12px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              borderLeft: '4px solid #a855f7'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#6b21a8' }}>
                {data.tmTickets.length}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>T&M Tickets</div>
            </div>
          </div>

          {/* Job Needs for Tomorrow */}
          {data.jobNeeds.length > 0 && (
            <Section title="Job Needs for Tomorrow" icon={<AlertTriangle size={18} />} count={data.jobNeeds.length} colorScheme="blue">
              {data.jobNeeds.map((need, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '8px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                    {need.foreman_name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                    {need.job_name}
                  </div>
                  {need.items && need.items.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569', fontSize: '14px' }}>
                      {need.items.map((item, i) => (
                        <li key={i}>{item.item_name_en}: {item.quantity}</li>
                      ))}
                    </ul>
                  )}
                  {need.job_needs_other_text && (
                    <div style={{ fontSize: '14px', color: '#475569', marginTop: '4px' }}>
                      Other: {need.job_needs_other_text}
                    </div>
                  )}
                </div>
              ))}
            </Section>
          )}

          {/* Daily Reports - Foreman */}
          {data.foremanReports.length > 0 && (
            <Section title="Daily Reports - Foreman" icon={<FileText size={18} />} count={data.foremanReports.length} colorScheme="green">
              {data.foremanReports.map(report => (
                <ReportCard
                  key={report.report_id}
                  title={report.job_name}
                  subtitle={`${report.bradanna_job_number} - ${report.submitted_by}`}
                  details={[
                    `Crew: ${report.crew_count}`,
                    `Loads In: ${report.total_loads_in} | Out: ${report.total_loads_out}`
                  ]}
                  shiftTimes={report.shift_times}
                  dayDescription={report.work_completed_description}
                  dayIssues={report.issues}
                  nightDescription={report.night_work_description}
                  nightIssues={report.night_issues}
                  formatShiftTime={formatShiftTime}
                  onViewPDF={report.pdf_url ? () => openPDF(report.pdf_url) : undefined}
                  onViewJobActivity={() => goToJobActivity(report.job_id)}
                  photos={report.photos}
                  jobName={report.job_name}
                  onPhotoClick={openCombinedLightbox}
                />
              ))}
            </Section>
          )}

          {/* Daily Reports - Admin */}
          {data.adminReports.length > 0 && (
            <Section title="Daily Reports - PM/Super" icon={<FileText size={18} />} count={data.adminReports.length} colorScheme="blue">
              {data.adminReports.map(report => (
                <ReportCard
                  key={report.report_id}
                  title={report.job_name}
                  subtitle={`${report.bradanna_job_number} - ${report.submitted_by}`}
                  details={[
                    `Crew: ${report.crew_count}`
                  ]}
                  shiftTimes={report.shift_times}
                  dayDescription={report.work_completed_description}
                  dayIssues={report.issues}
                  nightDescription={report.night_work_description}
                  nightIssues={report.night_issues}
                  formatShiftTime={formatShiftTime}
                  onViewPDF={report.pdf_url ? () => openPDF(report.pdf_url) : undefined}
                  onViewJobActivity={() => goToJobActivity(report.job_id)}
                  photos={report.photos}
                  jobName={report.job_name}
                  onPhotoClick={openCombinedLightbox}
                />
              ))}
            </Section>
          )}

          {/* Asphalt Reports */}
          {data.asphaltReports.length > 0 && (
            <Section title="Asphalt Reports" icon={<Truck size={18} />} count={data.asphaltReports.length} colorScheme="orange">
              {data.asphaltReports.map(report => (
                <ReportCard
                  key={report.report_id}
                  title={report.job_name}
                  subtitle={`${report.foreman_name} - ${report.plant_name || 'No plant'}`}
                  details={[
                    `Trucks: ${report.truck_count} | Loads: ${report.total_loads || 0}`,
                    report.work_description
                  ]}
                  shiftTimes={report.shift_times}
                  formatShiftTime={formatShiftTime}
                  onViewPDF={report.pdf_url ? () => openPDF(report.pdf_url) : undefined}
                  photos={report.photos}
                  jobName={report.job_name}
                  onPhotoClick={openCombinedLightbox}
                />
              ))}
            </Section>
          )}

          {/* T&M Tickets */}
          {data.tmTickets.length > 0 && (
            <Section title="T&M Tickets" icon={<Package size={18} />} count={data.tmTickets.length} colorScheme="purple">
              {data.tmTickets.map(ticket => (
                <ReportCard
                  key={ticket.ticket_id}
                  title={`#${ticket.ticket_number} - ${ticket.job_name}`}
                  subtitle={ticket.employee_name}
                  details={[
                    `Equipment: ${ticket.equipment_count}`,
                    ticket.notes
                  ]}
                  onViewPDF={ticket.pdf_url ? () => openPDF(ticket.pdf_url) : undefined}
                />
              ))}
            </Section>
          )}

          {/* Equipment Issues */}
          {data.equipmentIssues.length > 0 && (
            <Section title="Equipment Issues" icon={<Wrench size={18} />} count={data.equipmentIssues.length} colorScheme="amber">
              {data.equipmentIssues.map(issue => (
                <div
                  key={issue.issue_id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '8px',
                    borderLeft: `4px solid ${getSeverityColor(issue.severity)}`,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                    {issue.equipment_name} #{issue.bradanna_equipment_number}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                    {issue.job_name} - Reported by {issue.reporter_name}
                  </div>
                  <div style={{ fontSize: '14px', color: '#475569' }}>
                    {issue.issue_description}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    marginTop: '8px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    backgroundColor: `${getSeverityColor(issue.severity)}20`,
                    color: getSeverityColor(issue.severity)
                  }}>
                    {issue.severity}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* Silt Fence Issues */}
          {data.siltFenceIssues && data.siltFenceIssues.length > 0 && (
            <Section title="Silt Fence Issues" icon={<AlertCircle size={18} />} count={data.siltFenceIssues.length} colorScheme="red">
              {data.siltFenceIssues.map((issue: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '8px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                    {issue.job_name}
                  </div>
                  <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                    <span style={{ color: '#64748b' }}>Condition: </span>
                    <span style={{
                      color: issue.silt_fence_condition === 'good' ? '#16a34a' : '#dc2626',
                      fontWeight: 600
                    }}>
                      {issue.silt_fence_condition === 'good' ? 'Good' :
                       issue.silt_fence_condition === 'minor_repair' ? 'Minor Repair Needed' :
                       issue.silt_fence_condition === 'major_repair' ? 'Major Repair Needed' :
                       issue.silt_fence_condition === 'replacement' ? 'Replacement Needed' :
                       issue.silt_fence_condition === 'not_present' ? 'Not Present' :
                       issue.silt_fence_condition || 'N/A'}
                    </span>
                  </div>
                  {issue.silt_fence_notes && (
                    <div style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic', marginTop: '4px' }}>
                      {issue.silt_fence_notes}
                    </div>
                  )}
                </div>
              ))}
            </Section>
          )}

          {/* Overtime Summary */}
          {data.overtimeByJob && (() => {
            const otJobs = Object.entries(data.overtimeByJob).filter(([_, v]) => v.overtime > 0);
            if (otJobs.length === 0) return null;
            const totalOT = otJobs.reduce((s, [_, v]) => s + v.overtime, 0);
            const jobNames: Record<string, string> = {};
            [...data.foremanReports, ...data.adminReports].forEach(r => { jobNames[r.job_id] = r.job_name; });
            return (
              <Section title="Overtime Today" icon={<AlertTriangle size={18} />} count={undefined} colorScheme="amber">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '12px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>
                    <strong style={{ color: '#d97706' }}>Total Overtime:</strong>{' '}
                    <span style={{ color: '#d97706', fontWeight: 700 }}>{totalOT.toFixed(1)}h</span>
                  </div>
                  {otJobs.map(([jobId, v]) => (
                    <div key={jobId} style={{ fontSize: '13px', color: '#64748b', margin: '4px 0', paddingLeft: '12px' }}>
                      {(v as any).job_name || jobNames[jobId] || `Job #${jobId}`}:{' '}
                      <span style={{ color: '#d97706', fontWeight: 600 }}>{v.overtime.toFixed(1)}h OT</span>
                    </div>
                  ))}
                </div>
              </Section>
            );
          })()}
        </>
      )}

      {/* Combined photo slider — page-level so all reports share one looping viewer */}
      {lightboxOpen && allSlides.length > 0 && (
        <CombinedSliderModal
          slides={allSlides}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

// Section Component with email-style colors
interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  count?: number;
  colorScheme: 'green' | 'blue' | 'orange' | 'purple' | 'amber' | 'red';
}

const sectionColors = {
  green: { bg: '#f0fdf4', border: '#10b981', text: '#065f46', badge: '#d1fae5' },
  blue: { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', badge: '#dbeafe' },
  orange: { bg: '#fff7ed', border: '#ea580c', text: '#9a3412', badge: '#fed7aa' },
  purple: { bg: '#faf5ff', border: '#a855f7', text: '#6b21a8', badge: '#e9d5ff' },
  amber: { bg: '#fffbeb', border: '#f59e0b', text: '#92400e', badge: '#fef3c7' },
  red: { bg: '#fef2f2', border: '#dc2626', text: '#991b1b', badge: '#fee2e2' }
};

const Section: React.FC<SectionProps> = ({ title, icon, children, count, colorScheme }) => {
  const colors = sectionColors[colorScheme];
  return (
    <div style={{
      marginBottom: '20px',
      backgroundColor: colors.bg,
      borderLeft: `4px solid ${colors.border}`,
      borderRadius: '8px',
      padding: '16px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.text }}>
          {icon}
          <h2 style={{ fontSize: '16px', fontWeight: '700', margin: 0 }}>{title}</h2>
        </div>
        {count !== undefined && (
          <span style={{
            backgroundColor: colors.badge,
            color: colors.text,
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '700'
          }}>
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );
};

// Report Card Component
const ReportCard: React.FC<{
  title: string;
  subtitle: string;
  details: (string | null | undefined)[];
  shiftTimes?: { day_shift: { start: string; end: string } | null; night_shift: { start: string; end: string } | null };
  dayDescription?: string | null;
  dayIssues?: string | null;
  nightDescription?: string | null;
  nightIssues?: string | null;
  formatShiftTime?: (iso: string) => string;
  onViewPDF?: () => void;
  onViewJobActivity?: () => void;
  photos?: Photo[];
  jobName?: string;
  onPhotoClick?: (photoId: number) => void;
}> = ({ title, subtitle, details, shiftTimes, dayDescription, dayIssues, nightDescription, nightIssues, formatShiftTime, onViewPDF, onViewJobActivity, photos, jobName, onPhotoClick }) => {
  const hasPhotos = photos && photos.length > 0;

  const openModal = (index: number) => {
    if (onPhotoClick && photos && photos[index]) {
      onPhotoClick(photos[index].photo_id);
    }
  };

  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
          {title}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
          {subtitle}
        </div>
        {/* Shift times */}
        {shiftTimes && formatShiftTime && (shiftTimes.day_shift || shiftTimes.night_shift) && (
          <div style={{ marginBottom: '8px' }}>
            {shiftTimes.night_shift && (
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#6366f1', marginBottom: '2px' }}>
                Night: {formatShiftTime(shiftTimes.night_shift.start)} - {shiftTimes.night_shift.end ? formatShiftTime(shiftTimes.night_shift.end) : 'Still Clocked In'}
              </div>
            )}
            {shiftTimes.day_shift && (
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669', marginBottom: '2px' }}>
                Day: {formatShiftTime(shiftTimes.day_shift.start)} - {shiftTimes.day_shift.end ? formatShiftTime(shiftTimes.day_shift.end) : 'Still Clocked In'}
              </div>
            )}
          </div>
        )}

        {details.filter(Boolean).map((detail, idx) => (
          <div key={idx} style={{ fontSize: '14px', color: '#475569', marginBottom: '4px' }}>
            {detail}
          </div>
        ))}

        {/* Day/Night descriptions */}
        {(dayDescription || nightDescription) && (
          <div style={{ marginTop: '8px' }}>
            {dayDescription && (
              <div style={{ marginBottom: nightDescription ? '8px' : '0' }}>
                {nightDescription && (
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#059669', marginBottom: '2px' }}>Day Work:</div>
                )}
                <div style={{ fontSize: '14px', color: '#475569' }}>{dayDescription}</div>
              </div>
            )}
            {nightDescription && (
              <div>
                {dayDescription && (
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6366f1', marginBottom: '2px' }}>Night Work:</div>
                )}
                <div style={{ fontSize: '14px', color: '#475569' }}>{nightDescription}</div>
              </div>
            )}
          </div>
        )}

        {/* Day/Night issues */}
        {(dayIssues || nightIssues) && (
          <div style={{ marginTop: '6px' }}>
            {dayIssues && (
              <div style={{ fontSize: '14px', color: '#dc2626', marginBottom: nightIssues ? '6px' : '0' }}>
                <strong>{nightIssues ? 'Day Issues: ' : 'Issues: '}</strong>{dayIssues}
              </div>
            )}
            {nightIssues && (
              <div style={{ fontSize: '14px', color: '#7c3aed' }}>
                <strong>{dayIssues ? 'Night Issues: ' : 'Issues: '}</strong>{nightIssues}
              </div>
            )}
          </div>
        )}

        {/* Action buttons row */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          {onViewPDF && (
            <button
              onClick={onViewPDF}
              style={{
                padding: '6px 12px',
                backgroundColor: '#f1f5f9',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FileText size={14} />
              View PDF
            </button>
          )}
          {onViewJobActivity && (
            <button
              onClick={onViewJobActivity}
              style={{
                padding: '6px 12px',
                backgroundColor: '#f1f5f9',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <ChevronRight size={14} />
              Job Activity
            </button>
          )}

        </div>

        {/* Inline photo thumbnails */}
        {hasPhotos && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
            gap: '8px',
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #e5e7eb'
          }}>
            {photos.map((photo, idx) => (
              <div
                key={photo.photo_id}
                onClick={() => openModal(idx)}
                style={{
                  aspectRatio: '1',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor: '#e5e7eb'
                }}
              >
                <img
                  src={photo.thumbnail_url || photo.photo_url}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  alt={photo.caption || 'Photo'}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photos open in the page-level CombinedSliderModal — see DailySummaryViewer */}
    </>
  );
};

export default DailySummaryViewer;
